import { useEffect, useState } from 'react';

import { AutocompletePrediction } from 'components/ucl/autocomplete-options-dropdown';
import { useGetDeliveryAddressSuggestionsLazyQuery } from 'generated/graphql-gateway';
import { isUS, region } from 'utils/environment';
import logger from 'utils/logger';

import { MAX_ADDRESS_RESULTS } from './constants';
import { deliverySuggestionToPlaceData } from './delivery-suggestion-to-place-data';

const LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS: number = 3;

function useLocationPredictionsDelivery(location: string, { enabled = true }) {
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [fetchDeliveryAddressSuggestions] = useGetDeliveryAddressSuggestionsLazyQuery();

  useEffect(() => {
    if (!enabled) {
      setPredictions(() => []);
      return;
    }

    if (!location || location.length < LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS) {
      setPredictions(() => []);
      return;
    }

    (async () => {
      try {
        const { data } = await fetchDeliveryAddressSuggestions({
          variables: {
            input: {
              inputAddress: location,
              ...(region ? { countryCode: region } : {}),
              maxResults: MAX_ADDRESS_RESULTS,
            },
          },
        });

        const results: AutocompletePrediction[] =
          data?.deliveryAddressAutocomplete?.results
            ?.map((result, index) => {
              const address = result.address;
              const description = address?.formatted_address?.trim();

              if (!description) {
                return null;
              }

              const [descriptionSelection] = description ? description.split(',') : [];
              const mainText =
                address.street?.trim() || descriptionSelection?.trim() || description;

              const countryText = isUS ? 'USA' : 'Canada';
              const secondaryParts = [
                address.city?.trim(),
                address.state?.trim(),
                address.zip_code?.trim(),
                countryText,
              ].filter(Boolean);

              const placeId = `${description}-${address?.zip_code ?? index}`;
              return {
                placeId,
                description,
                mainText,
                secondaryText: secondaryParts.join(', '),
                placeData: deliverySuggestionToPlaceData(address),
              };
            })
            .filter(Boolean) ?? [];
        setPredictions(results);
      } catch (error) {
        setPredictions([]);
        logger.warn({ message: 'useLocationPredictionsDelivery error', error });
      }
    })();
  }, [enabled, fetchDeliveryAddressSuggestions, location]);

  return predictions;
}

export default useLocationPredictionsDelivery;
